import { cssVar, Flex, HStack, Icon, Box, InlineBanner, Text } from '@cad-arc';
import { useMemo } from 'react';
import { parseISO, format } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import { ApiFederatedQueryResponse } from 'mark43-federated-search/dist/lib/generated/api-types/models-federated-query-ApiFederatedQueryResponse';
import { LinkedEventNumber } from './dex-results-linked-event';
import { FR_EVENT_DETAILS_TABS } from '../../fr-event-details-page/constants';
import { useOpenEvent } from '../../common/hooks/use-open-event';
import {
    getAgencyEventFromFederatedSearchQueryLinks,
    getFederatedQueryLinks,
    getUnitCallSignFromFederatedSearchQueryLinks,
} from './utils';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { FederatedSearchQueryLinkDto } from '@mark43/cad-api';
import { colors } from '../../colors/colors';

export function FederatedSearchPreviewRow({
    previewSummary,
    federatedQuery,
    federatedSearchQueryLinkDtos,
    isLinkedEntitiesLoading,
    isLinkedEntitiesError,
    isWarning,
    isDanger,
}: {
    previewSummary: string;
    federatedQuery: ApiFederatedQueryResponse;
    federatedSearchQueryLinkDtos?: FederatedSearchQueryLinkDto[];
    isLinkedEntitiesLoading: boolean;
    isLinkedEntitiesError: boolean;
    isWarning?: boolean;
    isDanger?: boolean;
}) {
    const handleEventLinkClick = useOpenEvent();

    const federatedQueryLinks = useMemo(
        () => getFederatedQueryLinks(federatedSearchQueryLinkDtos, federatedQuery.id),
        [federatedSearchQueryLinkDtos, federatedQuery.id]
    );

    const createdDate = useMemo(() => {
        const rawDate = federatedQuery.auditMetadata?.createdDateUtc;
        return rawDate ? format(parseISO(rawDate), 'MM/dd/yy HH:mm') : 'Unknown date';
    }, [federatedQuery]);

    const callSign = useMemo(
        () => getUnitCallSignFromFederatedSearchQueryLinks(federatedQueryLinks),
        [federatedQueryLinks]
    );

    const { agencyEventNumber, agencyEventId } = useMemo(
        () => getAgencyEventFromFederatedSearchQueryLinks(federatedQueryLinks),
        [federatedQueryLinks]
    );

    const shouldShowExclamation = isWarning || isDanger;

    return (
        <Box width="100%" display="flex" flexDirection="column" gap={cssVar('arc.space.1')}>
            <Box display="flex" justifyContent="space-between">
                <Text fontWeight="bold">{previewSummary}</Text>
                {shouldShowExclamation && (
                    <Icon icon="ExclamationPoint" color={cssVar('arc.colors.negative.default')} />
                )}
            </Box>
            {isLinkedEntitiesError ? (
                <Flex h="100%" justifyContent="center" alignItems="center">
                    <InlineBanner
                        status="error"
                        title="Error"
                        description={errorOccurredWhile(
                            'loading CAD event and/or unit information'
                        )}
                    />
                </Flex>
            ) : (
                <HStack alignItems="flex-start" spacing={1} w="100%">
                    {isLinkedEntitiesLoading ? (
                        <Box width="65%">
                            <Skeleton
                                baseColor={colors.skeletonBaseColor}
                                highlightColor={colors.skeletonHighlightColor}
                                style={{ opacity: 0.5 }}
                            />
                        </Box>
                    ) : (
                        <>
                            {agencyEventNumber && (
                                <LinkedEventNumber
                                    onClick={() =>
                                        handleEventLinkClick({
                                            eventId: agencyEventId,
                                            frEventDetailsTabName: FR_EVENT_DETAILS_TABS.NARRATIVE,
                                        })
                                    }
                                >
                                    #{agencyEventNumber}
                                </LinkedEventNumber>
                            )}

                            <Text color="tertiary" as="span">
                                {createdDate}
                            </Text>

                            {callSign && (
                                <Text color="tertiary" as="span">
                                    by {callSign}
                                </Text>
                            )}
                        </>
                    )}
                </HStack>
            )}
        </Box>
    );
}
